import { Button } from '@shadcn/components/ui/button';

const CategoryList = ({ 
  categories, 
  toleranceSettings, 
  selectedCategory, 
  onSelectCategory, 
  getToleranceForCategory 
}) => {
  const getTotalCategories = () => categories.length;
  const getConfiguredCategories = () => toleranceSettings.length;
  const getPendingCategories = () => categories.length - toleranceSettings.length;

  const renderCategoryItem = (category) => {
    const tolerance = getToleranceForCategory(category.name);
    const isSelected = selectedCategory === category.name;
    const hasToleranceSet = !!tolerance;

    return (
      <div
        key={category._id}
        onClick={() => onSelectCategory(category.name)}
        className={`
          p-4 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-sm
          ${isSelected 
            ? 'border-gray-900 bg-gray-50 shadow-sm' 
            : 'border-gray-200 hover:border-gray-300'
          }
        `}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`
              w-3 h-3 rounded-full flex-shrink-0
              ${hasToleranceSet ? 'bg-green-500' : 'bg-red-400'}
            `} />
            
            <div className="min-w-0 flex-1">
              <p className={`
                font-medium truncate
                ${isSelected ? 'text-gray-900' : 'text-gray-700'}
              `}>
                {category.name}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {category.type || 'jifi'} • {category.isActive !== false ? 'Active' : 'Inactive'}
              </p>
            </div>
          </div>

          <div className="text-right flex-shrink-0">
            {hasToleranceSet ? (
              <div className="text-right">
                <p className="text-sm font-semibold text-green-700">
                  {tolerance.tolerancePercent}%
                </p>
                <p className="text-xs text-gray-500">
                  ${tolerance.toleranceValue}
                </p>
              </div>
            ) : (
              <div className="text-xs text-red-600 font-medium">
                Not Set
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!categories.length) {
    return (
      <div className="h-full flex flex-col">
        <div className="border-b border-gray-200 pb-4 mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Categories</h3>
          <p className="text-sm text-gray-600 mt-1">Select a category to configure tolerance</p>
        </div>
        
        <div className="flex-1 flex flex-col justify-center items-center py-8">
          <div className="text-4xl mb-4 text-gray-300">📂</div>
          <p className="text-gray-600 font-medium">No categories found</p>
          <p className="text-sm text-gray-500 mt-1">Categories will appear here when available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header with Stats */}
      <div className="border-b border-gray-200 pb-4 mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Categories</h3>
        <p className="text-sm text-gray-600 mt-1">Select a category to configure tolerance</p>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-2 mt-4">
          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg text-center">
            <div className="text-lg font-bold text-blue-700">{getTotalCategories()}</div>
            <div className="text-xs text-blue-600">Total</div>
          </div>
          <div className="bg-green-50 border border-green-200 p-3 rounded-lg text-center">
            <div className="text-lg font-bold text-green-700">{getConfiguredCategories()}</div>
            <div className="text-xs text-green-600">Configured</div>
          </div>
          <div className="bg-red-50 border border-red-200 p-3 rounded-lg text-center">
            <div className="text-lg font-bold text-red-700">{getPendingCategories()}</div>
            <div className="text-xs text-red-600">Pending</div>
          </div>
        </div>
      </div>

      {/* Filter Buttons */}
      <div className="flex gap-2 mb-4">
        <Button 
          size="sm" 
          variant="outline" 
          className="text-xs border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          All ({getTotalCategories()})
        </Button>
        <Button 
          size="sm" 
          variant="outline" 
          className="text-xs border-green-300 text-green-700 hover:bg-green-50"
        >
          ✅ Set ({getConfiguredCategories()})
        </Button>
        <Button 
          size="sm" 
          variant="outline" 
          className="text-xs border-red-300 text-red-700 hover:bg-red-50"
        >
          ❌ Not Set ({getPendingCategories()})
        </Button>
      </div>

      {/* Category List */}
      <div className="flex-1 space-y-3 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 400px)' }}>
        {categories
          .filter(cat => cat.isActive !== false) // Show active categories (including undefined)
          .map(renderCategoryItem)}
      </div>

      {/* Legend */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="text-xs text-gray-600">
          <div className="flex items-center gap-2 mb-1">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span>Tolerance configured</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
            <span>No tolerance set</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryList;